import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import colors from '@c2fo/liquidity/colors';
import { SyncIcon } from '@c2fo/liquidity/icons';
import FourHundred from '@/shared/assets/400.svg?url';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { IntercomSupportButtonForErrorPage } from '@/shared/pages/SomethingsWrong';

interface OfferBuilderErrorStateProps {
  pageHeading?: string;
  secondaryHeading?: string;
  additionalActions?: ReactNode;
}

/**
 * Shared error state component for offer builder and acquisition flows.
 * Displays a 400 error illustration with contextual recovery actions.
 */
export const OfferBuilderErrorState = ({
  pageHeading,
  secondaryHeading,
  additionalActions,
}: OfferBuilderErrorStateProps) => {
  const { t } = useTranslation();
  const hasAdditionalActions = Boolean(additionalActions);

  return (
    <div>
      {pageHeading && (
        <div className="flex flex-col items-start justify-between gap-6 pb-10 md:flex-row md:items-center">
          <h1 className="m-0 text-4xl">{pageHeading}</h1>
        </div>
      )}
      <div className="m-auto px-8 md:max-w-6xl">
        <div className="flex flex-1 flex-col items-center justify-evenly space-y-12 p-0 lg:flex-row lg:space-y-0 lg:pt-8">
          <img src={FourHundred} alt="400 Error" className="max-w-sm p-8" />
          <div className="max-w-2xl pb-4 lg:p-0">
            <h1 className="mt-0 text-4xl font-bold">{t('core.encounteredError')}</h1>
            {secondaryHeading && <h3 className="text-2xl font-medium">{secondaryHeading}</h3>}
            <h5 className="text-xl font-normal text-text-secondary">{t('somethingsWrong.message')}</h5>
          </div>
        </div>
        <div>
          <p className="mb-3 font-medium">{t('somethingsWrong.subheading2')}</p>
          <div className="flex flex-col gap-6 lg:flex-row">
            <div
              className={`flex items-center justify-between gap-4 rounded-md bg-white px-6 py-4 text-left shadow-md ${
                hasAdditionalActions ? 'lg:w-1/3' : 'lg:w-1/2'
              }`}
            >
              <div>
                <p className="text-sm font-medium">{t('core.refreshThePage')}</p>
                <p className="text-sm text-text-secondary">{t('core.refreshThePageDescription')}</p>
              </div>
              <button
                className="rounded-md p-4 hover:bg-gray-100"
                onClick={() => window.location.reload()}
                type="button"
              >
                <SyncIcon className="h-7 w-auto lg:block" fill={colors.blue[800]} />
              </button>
            </div>
            {additionalActions}
            <div
              className={`flex items-center justify-between gap-4 rounded-md bg-white px-6 py-4 text-left shadow-md ${
                hasAdditionalActions ? 'lg:w-1/3' : 'lg:w-1/2'
              }`}
            >
              <div>
                <p className="text-sm font-medium">{t('somethingsWrong.cta2Heading')}</p>
                <p className="text-sm text-text-secondary">{t('somethingsWrong.cta2Message')}</p>
              </div>
              {/* Wrapped with extra boundaries to be extra safe, as more deps are used in here */}
              <QueryBoundaries>
                <IntercomSupportButtonForErrorPage />
              </QueryBoundaries>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
